// `<favorite-toggle>` as a LitElement written in plain JavaScript: reactive properties,
// styles, and shadow options are declared with `static` class fields
// (`static properties`/`styles`/`shadowRootOptions`), so the element needs no build step.
//
// The element is store-agnostic: it never imports the store, renders purely from its
// properties, and on activation emits a `favorite-change` event whose `pressed` is the
// REQUESTED next state. The owning view bridges that to the store and reflects the
// result back by setting `pressed` (external state in, event out), which keeps the
// data-flow one-directional. The event bubbles and is composed so a single delegated
// listener on the view root catches it across the shadow boundary.

import { LitElement, html, css } from "lit";

export class FavoriteToggle extends LitElement {
  // Lit wires attribute-property reflection from this static map. `pressed` reflects to the
  // attribute so `:host([pressed])` CSS and the schedule view's host-keyed focus selector
  // keep working.
  static properties = {
    sessionId: { type: Number, attribute: "session-id" },
    pressed: { type: Boolean, reflect: true },
  };

  // Constructable stylesheet (CSP-clean: no inline `<style>`).
  static styles = css`
    :host {
      display: inline-flex;
    }
    button {
      display: inline-grid;
      place-items: center;
      inline-size: 2.25rem;
      block-size: 2.25rem;
      padding: 0;
      border: 1px solid var(--color-border, #d4d4d8);
      border-radius: var(--radius-full, 9999px);
      background-color: var(--color-surface, #fff);
      color: var(--color-muted, #71717a);
      cursor: pointer;
      line-height: 1;
      transition:
        color 0.15s ease,
        border-color 0.15s ease,
        background-color 0.15s ease;
    }
    button:hover {
      color: var(--color-accent, #6366f1);
      border-color: color-mix(in srgb, var(--color-accent, #6366f1) 40%, var(--color-border, #d4d4d8));
    }
    button:focus-visible {
      outline: 2px solid var(--color-accent, #6366f1);
      outline-offset: 2px;
    }
    :host([pressed]) button {
      color: #f59e0b;
      border-color: color-mix(in srgb, #f59e0b 45%, var(--color-border, #d4d4d8));
      background-color: color-mix(in srgb, #f59e0b 12%, var(--color-surface, #fff));
    }
    .icon {
      font-size: var(--text-lg, 1.125rem);
    }
    /* The slotted label is for assistive tech; the star carries the visual. */
    .label {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      white-space: nowrap;
      border: 0;
    }
  `;

  // `delegatesFocus` so `host.focus()` (the schedule view's focus restoration after a
  // re-render) lands on the inner button, and so the host is the natural
  // `document.activeElement` while focus lives in the shadow.
  static shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

  constructor() {
    super();
    this.sessionId = 0;
    this.pressed = false;
  }

  // Preserve a SYNCHRONOUS first render. Consumers depend on it: the schedule view's
  // focus-restore calls `host.focus()` right after re-creating the toggle (with
  // `delegatesFocus` there must already be a focusable button to receive it), and the
  // host must occupy real layout space immediately (no zero-size flash that shifts the
  // page). Lit defers the first render to a microtask, so flush it here. (Property-driven
  // RE-renders stay async, Lit's normal batching.)
  connectedCallback() {
    super.connectedCallback();
    if (this.isUpdatePending) {
      this.performUpdate();
    }
  }

  render() {
    return html`
      <button
        type="button"
        part="button"
        aria-pressed=${this.pressed ? "true" : "false"}
        title=${this.pressed ? "In My Agenda — click to remove" : "Add to My Agenda"}
        @click=${this.#onClick}
      >
        <span class="icon" aria-hidden="true">${this.pressed ? "★" : "☆"}</span>
        <span class="label"><slot></slot></span>
      </button>
    `;
  }

  // Emit the intent to flip favorite state. We do NOT mutate our own `pressed`: the host
  // owns the source of truth (the store) and reflects the result back. Arrow field so the
  // bound reference is stable across renders.
  #onClick = () => {
    this.dispatchEvent(
      new CustomEvent("favorite-change", {
        detail: { sessionId: this.sessionId, pressed: !this.pressed },
        bubbles: true,
        composed: true,
      }),
    );
  };
}

// Typed factory: callers get `.sessionId`/`.pressed` typed WITHOUT augmenting
// `HTMLElementTagNameMap` (which would need a global type declaration).
export function createFavoriteToggle() {
  return  (document.createElement("favorite-toggle"));
}

// Build a fully-configured host the views can drop in. The slotted label is STABLE (it
// describes the action, not the state); `aria-pressed` on the inner button conveys the
// on/off state, so a host can update `pressed` in place without rewriting the text.
export function favoriteToggle({ sessionId, pressed, title, compact = false }) {
  const name = title ?? "this session";
  const node = createFavoriteToggle();
  node.sessionId = sessionId;
  node.pressed = pressed;
  // The schedule view restores keyboard focus after a full re-render via this selector;
  // keep it on the host (which is `document.activeElement` when focus lives in the
  // shadow, thanks to `delegatesFocus`).
  node.setAttribute("data-favorite-for", String(sessionId));
  if (compact) {
    node.setAttribute("compact", "");
  }
  node.textContent = `Add “${name}” to My Agenda`;
  return node;
}
